import React, { useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Wrench,
  CheckCircle2,
  AlertTriangle,
  GitMerge,
  Loader2,
  BarChart3,
  PackageCheck,
  PackageX,
  RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const EANCleanup = () => {
  const { token } = useAuth();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/api/superadmin/ean-cleanup/preview`, { headers });
      setPreviewData(res.data);
    } catch (err) {
      toast.error('Fehler beim Scan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFixes = async () => {
    if (!previewData?.fixable?.length) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/ean-cleanup/apply`, {}, { headers });
      setResult(res.data);
      setPreviewData(null);
      toast.success(res.data.message, { duration: 6000 });
    } catch (err) {
      toast.error('Fehler beim Anwenden: ' + (err.response?.data?.detail || err.message));
    } finally {
      setApplying(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">EAN-Bestandsbereinigung</h1>
          <p className="text-muted-foreground text-sm">
            Scannt die Datenbank nach abgeschnittenen EAN-8 Codes und ersetzt sie durch die korrekte 13-stellige EAN aus dem Master-Katalog.
          </p>
        </div>

        {/* Action Card */}
        <Card>
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={runPreview}
                disabled={loading || applying}
                variant="outline"
                className="gap-2"
                data-testid="ean-preview-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Datenbank scannen (Vorschau)
              </Button>

              {previewData && previewData.summary.fixable_count > 0 && (
                <Button
                  onClick={applyFixes}
                  disabled={applying || loading}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ean-apply-btn"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  {applying ? 'Wird angewendet…' : `${previewData.summary.fixable_count} Korrekturen anwenden`}
                </Button>
              )}

              {previewData && (
                <Button variant="ghost" size="icon" onClick={runPreview} disabled={loading} title="Erneut scannen">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Karten */}
        {previewData && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Gescannt', value: previewData.summary.total_scanned, icon: BarChart3, color: 'text-blue-600' },
              { label: 'Fixbar (EAN-Merge)', value: previewData.summary.fixable_count, icon: GitMerge, color: 'text-emerald-600' },
              { label: 'Verdächtig', value: previewData.summary.suspicious_count, icon: AlertTriangle, color: 'text-amber-600' },
              { label: 'Korrekt', value: previewData.summary.clean_count, icon: CheckCircle2, color: 'text-green-600' },
            ].map(s => (
              <Card key={s.label}>
                <CardContent className="p-4 flex items-center gap-3">
                  <s.icon className={`h-6 w-6 flex-shrink-0 ${s.color}`} />
                  <div>
                    <div className="text-2xl font-bold">{s.value}</div>
                    <div className="text-xs text-muted-foreground">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Fixbare EANs */}
        {previewData?.fixable?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <GitMerge className="h-5 w-5 text-emerald-600" />
                Automatisch fixbar – EAN-Merge
                <Badge className="bg-emerald-100 text-emerald-700 border-0 ml-1">{previewData.fixable.length}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Diese kurzen EANs haben eine passende vollständige EAN im Master-Katalog. Ein Klick auf „Korrekturen anwenden" ersetzt sie alle automatisch.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {previewData.fixable.map((f, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/15">
                  <PackageX className="h-4 w-4 text-red-500 flex-shrink-0 hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{f.product_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      <span className="text-red-600 line-through">{f.short_ean}</span>
                      <span className="mx-2">→</span>
                      <span className="text-emerald-700 font-semibold">{f.correct_ean}</span>
                      {f.master_name !== f.product_name && (
                        <span className="ml-2 text-muted-foreground">({f.master_name})</span>
                      )}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    {f.affected_count} {f.affected_count === 1 ? 'Eintrag' : 'Einträge'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Verdächtige EANs */}
        {previewData?.suspicious?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Verdächtig – Kein Master-Treffer
                <Badge className="bg-amber-100 text-amber-700 border-0 ml-1">{previewData.suspicious.length}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Diese EANs haben eine fehlerhafte Prüfziffer oder keine Entsprechung im Master-Katalog. Manuell prüfen.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {previewData.suspicious.map((s, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/15">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{s.product_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.ean}</p>
                  </div>
                  <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 flex-shrink-0">
                    {s.issue === 'bad_checksum' ? 'Prüfziffer ungültig' : 'Kein Master gefunden'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Kein Handlungsbedarf */}
        {previewData && previewData.summary.fixable_count === 0 && previewData.summary.suspicious_count === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-lg mb-1">Datenbank ist sauber</h3>
              <p className="text-muted-foreground text-sm">
                Alle {previewData.summary.total_scanned} Einträge haben korrekte EAN-Codes.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Ergebnis nach Apply */}
        {result && (
          <Card className="border-green-200 bg-green-50 dark:bg-green-900/15">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900 dark:text-green-100">{result.message}</p>
                  <div className="mt-3 space-y-1.5">
                    {result.applied_fixes.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <PackageCheck className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                        <span className="font-mono text-xs">
                          <span className="line-through text-muted-foreground">{f.short_ean}</span>
                          <span className="mx-1.5">→</span>
                          <span className="font-semibold text-green-700">{f.correct_ean}</span>
                        </span>
                        <span className="text-muted-foreground text-xs">{f.product_name} · {f.mappings_updated} Einträge</span>
                      </div>
                    ))}
                  </div>
                  <Button onClick={runPreview} variant="outline" size="sm" className="mt-4 gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" /> Erneut prüfen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
